import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { type AuthTokens, isApiError } from '@dorixona/api'
import type { AuthApi, RegistrationInput, TelegramResult } from './api'
import { toApiPhone } from './model'
import type { TelegramPending } from '../../app/session'
import { canShareContact, shareTelegramContact, telegramInitData } from '../../app/telegram'

export type AuthStep = 'phone' | 'code' | 'register'

export interface AuthFlow {
  step: AuthStep
  phone: string
  setPhone: (digits: string) => void
  code: string
  setCode: (code: string) => void
  /** Ключ перевода ошибки последнего запроса, если он был неуспешным. */
  errorCode: string | undefined
  pending: boolean
  /** Секунд до повторной отправки кода; 0 — можно отправлять. */
  resendIn: number
  requestCode: () => void
  submitCode: () => void
  submitRegistration: (input: Omit<RegistrationInput, 'registrationToken'>) => void
  back: () => void
  /** Имя из Telegram — подставляется в регистрацию. */
  firstName: string
  /** Можно взять номер из Telegram вместо кода (только в мини-аппе). */
  canShare: boolean
  sharing: boolean
  shareContact: () => void
}

interface Options {
  api: AuthApi
  onAuthenticated: (tokens: AuthTokens) => void
  /** Мини-апп не вошёл сам — с какого шага продолжить (BR-39). */
  telegram?: TelegramPending | null
}

/** Контакт уходит боту, а бот — на сервер: ответ меняется не сразу. */
const CONTACT_TRIES = 5
const CONTACT_PAUSE_MS = 1200

const pause = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/** Отказ поделиться номером — не сбой сети, у него свой текст. */
class ContactError extends Error {
  constructor(readonly code: string) {
    super(code)
  }
}

function errorCodeOf(error: unknown): string {
  if (error instanceof ContactError) return error.code
  return isApiError(error) ? error.code : 'network'
}

/**
 * Состояние входа: телефон → код → (для нового номера) регистрация.
 * Шаг назад возвращает на предыдущий экран, а не выходит из приложения.
 */
export function useAuthFlow({ api, onAuthenticated, telegram = null }: Options): AuthFlow {
  const [step, setStep] = useState<AuthStep>(telegram?.kind === 'register' ? 'register' : 'phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const challengeId = useRef('')
  // isPending виден только после ре-рендера: двойной клик успевает вызвать
  // mutate дважды, и второй запрос ловит 429. Ref закрывает окно сразу.
  const requesting = useRef(false)
  const registrationToken = useRef(telegram?.kind === 'register' ? telegram.registrationToken : '')
  // В мини-аппе код идёт через Telegram-ручки: они и привязывают Telegram к номеру.
  const telegramToken = telegram?.kind === 'phone' ? telegram.telegramToken : ''

  // Таймер повторной отправки: тикает раз в секунду и сам останавливается.
  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setTimeout(() => {
      setResendIn((left) => left - 1)
    }, 1000)
    return () => {
      clearTimeout(timer)
    }
  }, [resendIn])

  const request = useMutation({
    mutationFn: () =>
      telegramToken
        ? api.telegramOtp(telegramToken, toApiPhone(phone))
        : api.requestOtp(toApiPhone(phone)),
    onSuccess: (challenge) => {
      challengeId.current = challenge.challengeId
      setResendIn(challenge.resendIn)
      setCode('')
      setStep('code')
    },
    onSettled: () => {
      requesting.current = false
    },
  })

  const verify = useMutation({
    mutationFn: () =>
      telegramToken
        ? api.telegramVerify(telegramToken, challengeId.current, code)
        : api.verifyOtp(challengeId.current, code),
    onSuccess: (result) => {
      if (result.status === 'ok') {
        onAuthenticated(result.tokens)
        return
      }
      registrationToken.current = result.registrationToken
      setStep('register')
    },
  })

  const register = useMutation({
    mutationFn: (input: Omit<RegistrationInput, 'registrationToken'>) =>
      api.register({ ...input, registrationToken: registrationToken.current }),
    onSuccess: onAuthenticated,
  })

  // Вызывается только когда код набран: кнопка до этого выключена, а
  // автоотправка срабатывает на четвёртой цифре.
  const submitCode = useCallback(() => {
    verify.mutate()
  }, [verify])

  // Поделились номером — спрашиваем сервер заново, пока бот его не привязал.
  const [shareFailed, setShareFailed] = useState(false)
  const share = useMutation({
    mutationFn: async (): Promise<TelegramResult> => {
      if (!(await shareTelegramContact())) throw new ContactError('telegram_contact_declined')
      for (let attempt = 1; ; attempt++) {
        const result = await api.telegramLogin(telegramInitData())
        if (result.status !== 'phone_required' || attempt >= CONTACT_TRIES) return result
        await pause(CONTACT_PAUSE_MS)
      }
    },
    onSuccess: (result) => {
      if (result.status === 'ok') {
        onAuthenticated(result.tokens)
        return
      }
      if (result.status === 'registration_required') {
        registrationToken.current = result.registrationToken
        setStep('register')
        return
      }
      share.reset()
      setShareFailed(true)
    },
  })

  const activeError = request.error ?? verify.error ?? register.error ?? share.error

  const back = useCallback(() => {
    // Регистрация начинается заново с телефона: код уже использован.
    setStep('phone')
    setCode('')
    request.reset()
    verify.reset()
    register.reset()
    share.reset()
    setShareFailed(false)
  }, [register, request, share, verify])

  return {
    step,
    phone,
    setPhone: (digits) => {
      request.reset()
      setPhone(digits)
    },
    code,
    setCode: (next) => {
      verify.reset()
      setCode(next)
    },
    errorCode: activeError
      ? errorCodeOf(activeError)
      : shareFailed
        ? 'telegram_contact_missing'
        : undefined,
    pending: request.isPending || verify.isPending || register.isPending || share.isPending,
    resendIn,
    requestCode: () => {
      if (requesting.current) return
      requesting.current = true
      request.mutate()
    },
    submitCode,
    submitRegistration: (input) => {
      register.mutate(input)
    },
    back,
    firstName: telegram?.firstName ?? '',
    canShare: telegramToken !== '' && canShareContact(),
    sharing: share.isPending,
    shareContact: () => {
      setShareFailed(false)
      request.reset()
      share.mutate()
    },
  }
}
